import React, { ComponentProps } from 'react';
import { Control, Controller, FieldValues, Path, UseControllerProps } from 'react-hook-form';
import { ErrorMessage, Text, TextInput } from '@sheinc/shelikes-design-system';
import { joinErrorMessages } from './joinErrorMessages';

type TextInputProps = ComponentProps<typeof TextInput>;

// ラベル行: ラベルの下に 8px あけ、行の高さはラベルテキストで決める
const labelStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 };
// Text の line-height は既定 180% のため、そのままだとラベル行が高くなり入力欄が下にずれる
const labelTextStyle: React.CSSProperties = { lineHeight: '100%' };
const footerStyle: React.CSSProperties = { marginTop: 4 };

type ReachHookFormProps<Values extends FieldValues> = {
  name: Path<Values>;
  control: Control<Values>;
  rules?: UseControllerProps<Values>['rules'];
};

type TextInputFieldProps = {
  label?: string;
  testId?: string;
};

type Props<Values extends FieldValues> = ReachHookFormProps<Values> &
  TextInputFieldProps &
  Pick<TextInputProps, 'type' | 'placeholder' | 'disabled'>;

export const TextInputField = <Values extends FieldValues>({
  name,
  control,
  rules,
  type,
  placeholder,
  label,
  disabled = false,
  testId,
}: Props<Values>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { ref: _, ...field }, fieldState: { error } }) => {
        const errorMessage = joinErrorMessages(error);
        const errorId = `${name}-error`;
        return (
          // testId はラベル・入力・エラーを含む要素に付ける（system spec はこの要素をエラー確認の範囲にしている）
          <div data-testid={testId}>
            {label && (
              <label style={labelStyle} htmlFor={name}>
                <Text as="span" size="M" weight="bold" style={labelTextStyle}>
                  {label}
                </Text>
              </label>
            )}
            <TextInput
              {...field}
              value={field.value ?? ''}
              id={name}
              type={type}
              placeholder={placeholder}
              disabled={disabled}
              error={!!errorMessage}
              aria-describedby={errorMessage ? errorId : undefined}
            />
            {errorMessage && (
              <div style={footerStyle}>
                <ErrorMessage id={errorId} show>
                  {errorMessage}
                </ErrorMessage>
              </div>
            )}
          </div>
        );
      }}
    ></Controller>
  );
};
